'use client';

import { type DependencyList, useEffect, useRef } from 'react';
import type { TempUploadItem, TempUploadRefs } from '../../types';

/**
 * Pure utils for tool temp upload: key format, refs/item types, list helpers.
 * The actual run loop lives in useRunTempUploadBatch (_hooks).
 */
export function toolTempUploadKey(type: string, ...parts: string[]): string {
  return ['tool-temp', type, ...parts].join(':');
}

/**
 * Owns processed/pending refs and effect; gets items via getItems(refs) then runs each
 * with dedup and error logging. List uploads pass getItems that uses
 * getToolTempXxxPendingItems(refs, createPromptListHelpers(...), ctx); source uploads
 * pass getItems = (refs) => getToolTempSourceXxxItems(refs, ctx).
 */
export function useRunTempUploadBatch(
  userInfo: unknown,
  logLabel: string,
  getItems: (refs: TempUploadRefs) => TempUploadItem[],
  deps: DependencyList,
): void {
  const processedRef = useRef<Set<string>>(new Set());
  const pendingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!userInfo) return;
    const refs: TempUploadRefs = { processed: processedRef, pending: pendingRef };
    const items = getItems(refs);
    const toRun = items.filter(
      (item) => !refs.processed.current.has(item.uploadKey) && !refs.pending.current.has(item.uploadKey),
    );
    if (toRun.length === 0) return;

    toRun.forEach(({ uploadKey, run }) => {
      refs.pending.current.add(uploadKey);
      run()
        .then(() => {
          refs.processed.current.add(uploadKey);
        })
        .catch((error) => {
          console.error(`${logLabel}:`, uploadKey, error);
        })
        .finally(() => {
          refs.pending.current.delete(uploadKey);
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getItems from closure; deps are caller-provided
  }, deps);
}
